import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { format } from 'date-fns'
import { CheckMarkIcon } from '@svgs'
import { toast } from 'react-toastify'
import { Button, MyModal } from 'src/components/ui'
import { formatPrice } from 'src/helpers/formatPrice'
import {
  IPromotionPlan,
  useGetPromotionPlansQuery,
  useGetPromotionPreviewQuery,
  usePromotePropertyMutation,
} from 'src/redux/api'

interface IPromoteModal {
  open: boolean
  handleClose: () => void
  propertyId: string
  title: string
  image?: string
  price?: { amount: number; currency: string } | null
}

const planName = (plan: IPromotionPlan, language: string) => {
  if (language.startsWith('ru')) return plan.nameRu
  if (language.startsWith('en')) return plan.nameEn
  return plan.nameUk
}

const planPrice = (plan: IPromotionPlan) => formatPrice(Math.round(plan.priceKop / 100), 'UAH')

// Self-serve TOP purchase with a live "how it will look" preview: the owner's
// card mocked at the head of the results list plus a position estimate for
// their city. Test mode settles instantly; live mode redirects to Monobank.
const PromoteModal: FC<IPromoteModal> = ({
  open,
  handleClose,
  propertyId,
  title,
  image,
  price,
}) => {
  const { t, i18n } = useTranslation()
  const { data: plansData, isLoading: plansLoading } = useGetPromotionPlansQuery(undefined, {
    skip: !open,
  })
  const { data: preview } = useGetPromotionPreviewQuery(propertyId, { skip: !open })
  const [promote, { isLoading: isBuying }] = usePromotePropertyMutation()
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [succeeded, setSucceeded] = useState(false)

  const plans = useMemo(() => plansData?.plans ?? [], [plansData?.plans])

  useEffect(() => {
    if (!selectedPlanId && plans.length > 0) setSelectedPlanId(plans[0].id)
  }, [plans, selectedPlanId])

  useEffect(() => {
    if (!open) setSucceeded(false)
  }, [open])

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId) ?? plans[0]
  const isActive = Boolean(preview?.isPromoted && preview?.promotedUntil)

  const handleBuy = async () => {
    if (!selectedPlan) return
    try {
      const result = await promote({ propertyId, planId: selectedPlan.id }).unwrap()
      if (result.payUrl) {
        window.location.assign(result.payUrl)
        return
      }
      setSucceeded(true)
      toast.success(t('promote.success_toast'))
    } catch {
      // toastMiddleware surfaces the backend error message
    }
  }

  return (
    <MyModal open={open} handleClose={handleClose} width="md:w-[44rem] w-[calc(100%_-_2rem)]">
      <div className="flex flex-col gap-4 p-2">
        {succeeded ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-success-soft flex items-center justify-center">
              <CheckMarkIcon className="w-8 h-8 text-success" />
            </div>
            <h3 className="text-lg font-black text-ink">{t('promote.success_title')}</h3>
            <p className="text-sm text-ink-2 max-w-[24rem]">{t('promote.success_text')}</p>
            <Button className="!h-11 mt-2" onClick={handleClose}>
              {t('promote.done')}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-black text-ink mr-auto">{t('promote.title')}</h3>
              {isActive && preview?.promotedUntil && (
                <span className="px-3 py-1.5 rounded-2xl bg-brand-soft text-sm font-bold text-brand-ink">
                  {t('promote.active_until', {
                    date: format(new Date(preview.promotedUntil), 'dd.MM.yyyy'),
                  })}
                </span>
              )}
            </div>
            <p className="text-sm text-ink-2">{t('promote.description')}</p>
            {plansData?.testMode && (
              <div className="px-3 py-2 rounded-xl bg-warning-soft border border-warning/25 text-sm text-warning">
                {t('promote.test_note')}
              </div>
            )}

            {plansLoading || !plansData ? (
              <div className="grid grid-cols-3 gap-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-24 rounded-xl bg-ink/10 animate-pulse" />
                ))}
              </div>
            ) : plans.length === 0 ? (
              <p className="text-sm text-ink-2">{t('promote.not_available')}</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {plans.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlanId(plan.id)}
                    className={clsx(
                      'flex flex-col items-center gap-1 p-3 rounded-xl border border-solid transition-colors duration-200',
                      selectedPlan?.id === plan.id
                        ? 'border-brand-strong bg-brand-soft'
                        : 'border-ink/[0.18] hover:border-brand',
                    )}
                  >
                    <span className="text-sm font-bold text-ink">
                      {planName(plan, i18n.language)}
                    </span>
                    <span className="text-xl font-black text-ink">{plan.durationDays}</span>
                    <span className="text-xs text-ink-2">{t('promote.days_suffix')}</span>
                    <span className="mt-1 text-sm font-black text-brand-ink">
                      {planPrice(plan)}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Live preview: the buyer's own card at the head of the feed */}
            <div className="flex flex-col gap-2 p-3 rounded-2xl bg-page border border-brand-soft">
              <span className="text-sm font-bold text-ink">{t('promote.preview_caption')}</span>
              <div className="flex items-center gap-3 p-2 bg-surface rounded-xl border-2 border-brand shadow-sm">
                {image ? (
                  <img src={image} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-ink/10 shrink-0" />
                )}
                <div className="flex flex-col gap-1 min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 text-[.625rem] font-black text-white bg-brand-strong rounded-lg">
                      {t('promote.badge_top')}
                    </span>
                    {price && (
                      <span className="text-sm font-black text-ink">
                        {formatPrice(price.amount, price.currency)}
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-ink-2 truncate">{title}</span>
                </div>
              </div>
              {[...Array(2)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-surface/60 rounded-xl">
                  <div className="w-14 h-14 rounded-xl bg-ink/10 shrink-0" />
                  <div className="flex flex-col gap-2 flex-1">
                    <div className="h-2.5 w-24 rounded bg-ink/10" />
                    <div className="h-2.5 w-40 rounded bg-ink/10" />
                  </div>
                </div>
              ))}
              {preview && (
                <p className="text-sm text-ink-2">
                  <strong>
                    {t('promote.preview_position', { position: preview.estimatedPosition })}
                  </strong>{' '}
                  {t('promote.preview_stats', {
                    promoted: preview.promotedInCity,
                    total: preview.totalActiveInCity,
                  })}
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                className="!h-11 flex-1"
                onClick={handleBuy}
                isLoading={isBuying}
                disabled={!selectedPlan || plans.length === 0}
              >
                {plansData?.testMode
                  ? t('promote.activate_test')
                  : t('promote.pay_button', { price: selectedPlan ? planPrice(selectedPlan) : '' })}
              </Button>
              <Button variant="outlined" className="!h-11" onClick={handleClose}>
                {t('promote.cancel')}
              </Button>
            </div>
            {!plansData?.testMode && (
              <p className="text-xs text-ink-2 text-center">{t('promote.pay_note')}</p>
            )}
          </>
        )}
      </div>
    </MyModal>
  )
}

export default PromoteModal
